import React, { useContext, useState } from 'react';
import { bool, object, shape, string } from 'prop-types';
import template from 'url-template';
import { Redirect, withRouter } from 'react-router-dom';

import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';

import MetaContext from './MetaContext';
import RuleDeleteConfirmModal from '../../containers/Modals/RuleDeleteConfirmModal';
import RuleDeleteSelectedModal from '../../containers/Modals/RuleDeleteSelectedModal';
import RuleDuplicateConfirmModal from '../../containers/Modals/RuleDuplicateConfirmModal';
import RuleDuplicateSelectedModal from '../../containers/Modals/RuleDuplicateSelectedModal';
import RuleRecalculateModal from '../../containers/Modals/RuleRecalculateModal';

import defaultClasses from './ContextMenu.module.css';

const ManageRuleUrl = template.parse('/rules/{id}/edit{?company}');
const RuleTargetsUrl = template.parse('/rules/{id}/targets{?company}');
const RuleExceptionsUrl = template.parse('/rules/{id}/exceptions{?company}');

const DEFAULT_MODAL_STATES = Object.freeze({
    delete:           false,
    deleteConfirm:    false,
    duplicate:        false,
    duplicateConfirm: false,
    recalculate:      false
});

const ContextMenu = ({ hidden, location, rule }) => {
    const { readOnly } = useContext(MetaContext);
    const [anchorEl, setAnchorEl] = useState(null);
    const [modalStates, setModalStates] = useState({ ...DEFAULT_MODAL_STATES });
    const [redirectTo, setRedirectTo] = useState(null);
    const open = Boolean(anchorEl);

    function getResetQuerystring() {
        const params = new URLSearchParams(location.search);
        params.delete('endTimestamp');
        params.delete('startTimestamp');
        params.delete('page');
        params.delete('pageSize');
        params.delete('orderby');
        return params;
    }

    if (hidden) {
        return null;
    }

    function handleClose() {
        setAnchorEl(null);
    }

    function handleDeleteConfirmation() {
        setModalStates({
            ...DEFAULT_MODAL_STATES,
            delete: true
        });
    }

    function handleDeleteRule() {
        setModalStates({
            ...DEFAULT_MODAL_STATES,
            deleteConfirm: true
        });
        handleClose();
    }

    function handleDuplicateConfirmation() {
        setModalStates({
            ...DEFAULT_MODAL_STATES,
            duplicate: true
        });
        handleClose();
    }

    function handleDuplicateRule() {
        setModalStates({
            ...DEFAULT_MODAL_STATES,
            duplicateConfirm: true
        });
        handleClose();
    }

    function handleManageRule() {
        setRedirectTo({
            pathname: ManageRuleUrl.expand(rule),
            search:   location.search
        });
    }

    function handleModalClose() {
        setModalStates({ ...DEFAULT_MODAL_STATES });
        handleClose();
    }

    function handleOpen(event) {
        setAnchorEl(event.currentTarget);
    }

    function handleRuleDebugger() {
        const params = getResetQuerystring();
        setRedirectTo({
            pathname: RuleExceptionsUrl.expand(rule),
            search:   params.toString()
        });
        handleClose();
    }

    function handleViewTargets() {
        setRedirectTo({
            pathname: RuleTargetsUrl.expand(rule),
            search:   location.search
        });
    }

    if (redirectTo) {
        return (
            <Redirect push to={redirectTo} />
        );
    }

    return (
        <>
            <Button
                aria-owns={open && 'render-props-menu'}
                aria-haspopup="true"
                onClick={handleOpen}
                className={defaultClasses.toggleButton}
            >
                <i className="fas fa-ellipsis-h" />
            </Button>
            <Menu
                id="render-props-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                <MenuItem
                    classes={{
                        root: defaultClasses.menuItem
                    }}
                    onClick={handleManageRule}
                >
                    <img
                        alt=""
                        className={defaultClasses.menuItemIcon}
                        src="/assets/images/SettingsFlat.svg"
                    />
                    Manage
                </MenuItem>
                <MenuItem
                    classes={{
                        root: defaultClasses.menuItem
                    }}
                    onClick={handleRuleDebugger}
                >
                    <img
                        alt=""
                        className={defaultClasses.menuItemIcon}
                        src="/assets/images/ruleDebuggerIconColor.svg"
                    />
                    Debugger
                </MenuItem>
                <MenuItem
                    classes={{
                        root: defaultClasses.menuItem
                    }}
                    onClick={handleViewTargets}
                >
                    <img
                        alt=""
                        className={defaultClasses.menuItemIcon}
                        src="/assets/images/targetColorIcon.svg"
                    />
                    View Targets
                </MenuItem>
                <MenuItem
                    classes={{
                        root: defaultClasses.menuItem
                    }}
                    disabled={readOnly}
                    onClick={handleDuplicateRule}
                >
                    <img
                        alt=""
                        className={defaultClasses.menuItemIcon}
                        src="/assets/images/duplicateIconColor.svg"
                    />
                    Duplicate
                </MenuItem>
                <MenuItem
                    classes={{
                        root: defaultClasses.menuItem
                    }}
                    disabled={readOnly}
                    onClick={handleDeleteRule}
                >
                    <img
                        alt=""
                        className={defaultClasses.menuItemIcon}
                        src="/assets/images/actionBarItem-delete.svg"
                    />
                    Delete
                </MenuItem>
            </Menu>
            <RuleDeleteConfirmModal
                onConfirm={handleDeleteConfirmation}
                onClose={handleModalClose}
                open={modalStates.deleteConfirm}
                rules={[rule]}
            />
            <RuleDeleteSelectedModal
                onClose={handleModalClose}
                open={modalStates.delete}
                rules={[rule]}
            />
            <RuleDuplicateConfirmModal
                onConfirm={handleDuplicateConfirmation}
                onClose={handleModalClose}
                open={modalStates.duplicateConfirm}
                rules={[rule]}
            />
            <RuleDuplicateSelectedModal
                onClose={handleModalClose}
                open={modalStates.duplicate}
                rules={[rule]}
            />
            <RuleRecalculateModal
                onClose={handleModalClose}
                open={modalStates.recalculate}
                rules={[rule]}
            />
        </>
    );
};

ContextMenu.defaultProps = {
    hidden: false
};

ContextMenu.propTypes = {
    hidden:   bool,
    location: object,
    rule:     shape({
        id: string
    })
};

export default withRouter(ContextMenu);
